import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, Eye, EyeOff, ImagePlus, Loader2, Save, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import { api, formatApiError, API_BASE } from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { confirmDialog } from "../components/ConfirmHost";
import { PrintOrderButton } from "../components/PrintOrderButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const defaultSubtitle = (trip) =>
  `${(trip.origin?.name || "").split(",")[0]} → ${(trip.destination?.name || "").split(",")[0]}${trip.round_trip ? " → thuis" : ""}`;

const fmtTime = (ts) => {
  try { return new Date(ts).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

const buildDays = (trip) => {
  const storyByDay = {};
  (trip.story?.days || []).forEach((d) => { storyByDay[d.day] = d.text || ""; });
  const edits = trip.photobook_edits || {};
  const dayDates = (trip.days || []).map((d) => d.date).filter(Boolean);
  const clampDate = (date) => {
    if (!date || !dayDates.length || dayDates.includes(date)) return date;
    return dayDates.reduce((best, dd) =>
      Math.abs(new Date(dd) - new Date(date)) < Math.abs(new Date(best) - new Date(date)) ? dd : best, dayDates[0]);
  };
  const photosByDate = {};
  const entriesByDate = {};
  [...(trip.log_entries || [])]
    .sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""))
    .forEach((e) => {
      const date = clampDate((e.timestamp || "").slice(0, 10));
      const ids = [
        ...(e.photo_id ? [e.photo_id] : []),
        ...(e.media || []).filter((m) => (m.content_type || "").startsWith("image")).map((m) => m.id),
      ];
      ids.forEach((pid) => { (photosByDate[date] = photosByDate[date] || []).push({ id: pid, extra: false }); });
      if ((e.text || "").trim()) (entriesByDate[date] = entriesByDate[date] || []).push({ id: e.id, time: e.timestamp, originalText: e.text });
    });
  Object.entries(edits.extra_photos || {}).forEach(([date, pids]) => {
    (pids || []).forEach((pid) => { (photosByDate[date] = photosByDate[date] || []).push({ id: pid, extra: true }); });
  });
  const entryEdits = edits.entry_edits || {};
  return (trip.days || []).map((d) => {
    const lay = (edits.photo_layout || {})[d.date] || {};
    const excluded = new Set(lay.excluded || []);
    let photos = (photosByDate[d.date] || []).map((p) => ({ ...p, excluded: excluded.has(p.id) }));
    if (lay.order?.length) {
      const pos = {};
      lay.order.forEach((pid, i) => { pos[pid] = i; });
      photos = [...photos].sort((a, b) => (pos[a.id] ?? 1e6) - (pos[b.id] ?? 1e6));
    }
    const entries = (entriesByDate[d.date] || []).map((en) => ({
      ...en,
      text: entryEdits[en.id]?.text ?? en.originalText,
      hidden: !!entryEdits[en.id]?.hidden,
    }));
    return {
      day_number: d.day_number,
      date: d.date,
      label: d.type === "stay"
        ? `Verblijf in ${(d.end?.name || "").split(",")[0]}`
        : `${(d.start?.name || "").split(",")[0]} → ${(d.end?.name || "").split(",")[0]}`,
      text: (edits.day_texts || {})[String(d.day_number)] ?? storyByDay[d.day_number] ?? "",
      originalText: storyByDay[d.day_number] ?? "",
      entries,
      photos,
    };
  });
};

const buildEdits = (days, cover, trip) => {
  const edits = { cover_title: null, cover_subtitle: null, day_texts: {}, entry_edits: {}, photo_layout: {} };
  if (cover.title.trim() && cover.title.trim() !== (trip.name || "")) edits.cover_title = cover.title.trim();
  if (cover.subtitle.trim() && cover.subtitle.trim() !== defaultSubtitle(trip)) edits.cover_subtitle = cover.subtitle.trim();
  days.forEach((d) => {
    if (d.text !== d.originalText) edits.day_texts[String(d.day_number)] = d.text;
    d.entries.forEach((en) => {
      if (en.hidden || en.text !== en.originalText)
        edits.entry_edits[en.id] = { text: en.text !== en.originalText ? en.text : null, hidden: en.hidden };
    });
    if (d.photos.length) {
      edits.photo_layout[d.date] = {
        order: d.photos.map((p) => p.id),
        excluded: d.photos.filter((p) => p.excluded).map((p) => p.id),
      };
    }
  });
  return edits;
};

export default function PhotobookEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [days, setDays] = useState([]);
  const [cover, setCover] = useState({ title: "", subtitle: "" });
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadingDay, setUploadingDay] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => { if (e.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const uploadRefs = useRef({});

  useEffect(() => {
    api.get(`/trips/${id}`).then(({ data }) => {
      setTrip(data);
      setDays(buildDays(data));
      const e = data.photobook_edits || {};
      setCover({ title: e.cover_title || data.name || "", subtitle: e.cover_subtitle || defaultSubtitle(data) });
    }).catch((err) => {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/dashboard");
    });
  }, [id, navigate]);

  const patchDay = (i, patch) => setDays((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const patchEntry = (i, entryId, patch) =>
    patchDay(i, { entries: days[i].entries.map((en) => (en.id === entryId ? { ...en, ...patch } : en)) });

  const movePhoto = (i, pi, dir) => {
    const photos = [...days[i].photos];
    const j = pi + dir;
    if (j < 0 || j >= photos.length) return;
    [photos[pi], photos[j]] = [photos[j], photos[pi]];
    patchDay(i, { photos });
  };

  const removeExtraPhoto = async (i, photo) => {
    if (!(await confirmDialog("Deze toegevoegde foto definitief uit je fotoboek verwijderen?", { title: "Foto verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    try {
      await api.delete(`/trips/${id}/photobook/photos/${photo.id}`);
      patchDay(i, { photos: days[i].photos.filter((p) => p.id !== photo.id) });
      toast.success("Foto verwijderd");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadPhotos = async (i, e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setUploadingDay(i);
    const added = [];
    for (const f of files.slice(0, 8)) {
      try {
        const small = await compressImage(f, 1800, 0.85).catch(() => f);
        const fd = new FormData();
        fd.append("file", small, f.name || "foto.jpg");
        fd.append("date", days[i].date);
        const { data } = await api.post(`/trips/${id}/photobook/photos`, fd, { timeout: 120000 });
        added.push({ id: data.id, extra: true, excluded: false });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Uploaden van '${f.name}' mislukt`);
      }
    }
    if (added.length) {
      setDays((ds) => ds.map((d, j) => (j === i ? { ...d, photos: [...d.photos, ...added] } : d)));
      toast.success(`${added.length} foto${added.length > 1 ? "'s" : ""} toegevoegd`);
    }
    setUploadingDay(null);
  };

  const save = async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/trips/${id}/photobook/edits`, buildEdits(days, cover, trip));
      if (!silent) toast.success("Fotoboek-aanpassingen opgeslagen");
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const fetchPdfBlob = async () => {
    if (!(await save(true))) return null;
    const res = await api.get(`/trips/${id}/export/photobook`, { responseType: "blob", timeout: 180000 });
    return res.data;
  };

  const saveBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `voltroute-fotoboek-${(trip?.name || "trip").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    const tid = toast.loading("Voorbeeld samenstellen — even geduld...");
    try {
      const blob = await fetchPdfBlob();
      if (blob) {
        setNumPages(0);
        setPageNum(1);
        setPreview(blob);
        toast.dismiss(tid);
      } else {
        toast.error("Voorbeeld maken mislukt", { id: tid });
      }
    } catch (err) {
      let msg = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail; } catch { msg = null; }
      }
      toast.error(formatApiError(msg) || "Voorbeeld maken mislukt", { id: tid });
    } finally {
      setPreviewLoading(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    const tid = toast.loading("Fotoboek samenstellen — even geduld...");
    try {
      const blob = await fetchPdfBlob();
      if (blob) {
        saveBlob(blob);
        toast.success("Fotoboek gedownload! 📖", { id: tid });
      } else {
        toast.error("Fotoboek maken mislukt", { id: tid });
      }
    } catch (err) {
      let msg = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail; } catch { msg = null; }
      }
      toast.error(formatApiError(msg) || "Fotoboek maken mislukt", { id: tid });
    } finally {
      setDownloading(false);
    }
  };

  if (!trip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="photobook-editor-loading">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="photobook-editor">
      <header className="sticky top-0 z-40 glass-panel border-b border-border/60">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <button data-testid="photobook-back-button" onClick={() => navigate(`/trip/${id}`)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-[color] duration-200 min-w-0">
            <ArrowLeft className="w-4 h-4 shrink-0" /> <span className="truncate hidden sm:inline">{trip.name}</span><span className="sm:hidden">Terug</span>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid="photobook-save-button" variant="outline" onClick={() => save()} disabled={saving || downloading || previewLoading}
              className="rounded-full active:scale-95 transition-[transform,background-color] duration-200">
              {saving ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" /> : <Save className="w-4 h-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Opslaan</span>
            </Button>
            <Button data-testid="photobook-preview-button" variant="outline" onClick={openPreview} disabled={saving || downloading || previewLoading}
              className="rounded-full active:scale-95 transition-[transform,background-color] duration-200">
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" /> : <Eye className="w-4 h-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Voorbeeld</span>
            </Button>
            <Button data-testid="photobook-download-button" onClick={download} disabled={saving || downloading || previewLoading}
              className="rounded-full bg-primary hover:bg-[#1B4332] active:scale-95 transition-[transform,background-color] duration-200">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" /> : <Download className="w-4 h-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Download PDF</span>
            </Button>
            <PrintOrderButton tripId={id} />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Fotoboek-editor</p>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Maak je fotoboek helemaal af</h1>
          <p className="text-sm text-muted-foreground mt-2">Kies per dag welke foto's in het boek komen, pas de volgorde en teksten aan, en voeg extra foto's toe. Je reisverslag en originele foto's blijven ongewijzigd.</p>
        </div>

        <div className="bg-card rounded-2xl p-5 space-y-4" data-testid="photobook-cover-card">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-primary">Cover</p>
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input data-testid="photobook-cover-title-input" value={cover.title} onChange={(e) => setCover((c) => ({ ...c, title: e.target.value }))} className="h-12 rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label>Ondertitel</Label>
            <Input data-testid="photobook-cover-subtitle-input" value={cover.subtitle} onChange={(e) => setCover((c) => ({ ...c, subtitle: e.target.value }))} className="h-12 rounded-xl" />
          </div>
        </div>

        {days.map((d, i) => {
          const included = d.photos.filter((p) => !p.excluded).length;
          return (
            <div key={d.day_number} className="bg-card rounded-2xl p-5 space-y-4" data-testid={`photobook-day-${d.day_number}`}>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <p className="font-heading font-bold">Dag {d.day_number} <span className="text-muted-foreground font-normal text-sm">— {d.date} · {d.label}</span></p>
                {d.photos.length > 0 && (
                  <span className={`text-xs font-semibold ${included > 12 ? "text-red-600" : "text-muted-foreground"}`} data-testid={`photobook-day-${d.day_number}-photo-count`}>
                    {included} foto's in het boek{included > 12 ? " (max 12 — de rest valt weg)" : ""}
                  </span>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Dagtekst in het boek</Label>
                  {d.text !== d.originalText && (
                    <button type="button" data-testid={`photobook-day-${d.day_number}-reset-text`} onClick={() => patchDay(i, { text: d.originalText })}
                      className="text-xs font-semibold text-accent hover:underline flex items-center gap-1"><Undo2 className="w-3 h-3" /> Herstel origineel</button>
                  )}
                </div>
                <Textarea data-testid={`photobook-day-${d.day_number}-text`} value={d.text} onChange={(e) => patchDay(i, { text: e.target.value })}
                  placeholder="Schrijf hier een tekst voor deze dag (leeg = geen tekst in het boek)" className="rounded-xl min-h-[90px]" />
              </div>

              {d.entries.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Journal-notities (oog = tonen/verbergen in het boek)</Label>
                  {d.entries.map((en) => (
                    <div key={en.id} className={`flex items-start gap-2 ${en.hidden ? "opacity-45" : ""}`}>
                      <span className="font-mono text-xs text-primary font-bold mt-2.5 w-11 shrink-0">{fmtTime(en.time)}</span>
                      <Textarea data-testid={`photobook-entry-${en.id}-text`} value={en.text} disabled={en.hidden}
                        onChange={(e) => patchEntry(i, en.id, { text: e.target.value })} className="rounded-xl min-h-[40px] text-sm flex-1" rows={1} />
                      <button type="button" data-testid={`photobook-entry-${en.id}-toggle`} title={en.hidden ? "Tonen in het boek" : "Verbergen in het boek"}
                        onClick={() => patchEntry(i, en.id, { hidden: !en.hidden })}
                        className="p-2 mt-1 rounded-full hover:bg-secondary transition-[background-color] duration-200 shrink-0">
                        {en.hidden ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-primary" />}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {d.photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {d.photos.map((p, pi) => (
                    <div key={p.id} className={`relative rounded-xl overflow-hidden group ${p.excluded ? "opacity-40 grayscale" : ""}`} data-testid={`photobook-photo-${p.id}`}>
                      <img src={`${API_BASE}/trips/${id}/photos/${p.id}?size=thumb`} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover bg-secondary" />
                      {p.extra && <span className="absolute top-1.5 left-1.5 text-[9px] font-bold bg-accent text-white rounded-full px-2 py-0.5">TOEGEVOEGD</span>}
                      {p.excluded && <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white bg-black/40">NIET IN BOEK</span>}
                      <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
                        <button type="button" data-testid={`photobook-photo-${p.id}-up`} title="Eerder in het boek" disabled={pi === 0}
                          onClick={() => movePhoto(i, pi, -1)}
                          className="w-7 h-7 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center disabled:opacity-30 transition-[background-color] duration-150">
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" data-testid={`photobook-photo-${p.id}-down`} title="Later in het boek" disabled={pi === d.photos.length - 1}
                          onClick={() => movePhoto(i, pi, 1)}
                          className="w-7 h-7 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center disabled:opacity-30 transition-[background-color] duration-150">
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        {p.extra ? (
                          <button type="button" data-testid={`photobook-photo-${p.id}-delete`} title="Foto verwijderen"
                            onClick={() => removeExtraPhoto(i, p)}
                            className="w-7 h-7 rounded-full bg-red-600/85 hover:bg-red-600 text-white flex items-center justify-center transition-[background-color] duration-150">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button type="button" data-testid={`photobook-photo-${p.id}-toggle`} title={p.excluded ? "Terug in het boek" : "Niet in het boek"}
                            onClick={() => patchDay(i, { photos: d.photos.map((q) => (q.id === p.id ? { ...q, excluded: !q.excluded } : q)) })}
                            className="w-7 h-7 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center transition-[background-color] duration-150">
                            {p.excluded ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <input ref={(el) => { uploadRefs.current[i] = el; }} type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => uploadPhotos(i, e)} data-testid={`photobook-day-${d.day_number}-upload-input`} />
              <button type="button" data-testid={`photobook-day-${d.day_number}-upload-button`} disabled={uploadingDay !== null}
                onClick={() => uploadRefs.current[i]?.click()}
                className="w-full rounded-xl border-2 border-dashed border-primary/40 hover:border-primary text-primary font-semibold text-sm py-2.5 flex items-center justify-center gap-2 transition-[border-color] duration-200 disabled:opacity-50">
                {uploadingDay === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                {uploadingDay === i ? "Foto's uploaden..." : "Extra foto's toevoegen aan deze dag"}
              </button>
            </div>
          );
        })}

        <div className="flex justify-end gap-2 pb-8">
          <Button data-testid="photobook-save-button-bottom" variant="outline" onClick={() => save()} disabled={saving || downloading || previewLoading} className="rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Opslaan
          </Button>
          <Button data-testid="photobook-preview-button-bottom" variant="outline" onClick={openPreview} disabled={saving || downloading || previewLoading} className="rounded-full">
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />} Voorbeeld
          </Button>
          <Button data-testid="photobook-download-button-bottom" onClick={download} disabled={saving || downloading || previewLoading} className="rounded-full bg-primary hover:bg-[#1B4332]">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />} Download PDF
          </Button>
          <PrintOrderButton tripId={id} variant="bottom" />
        </div>
      </main>

      {preview && (
        <div data-testid="photobook-preview-overlay" className="fixed inset-0 z-[1200] bg-[#0F172A]/92 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-4 sm:px-6 h-14 shrink-0">
            <p className="text-white/90 text-sm font-semibold truncate">Voorbeeld — {trip.name}</p>
            <div className="flex items-center gap-2">
              <Button data-testid="photobook-preview-download" size="sm" onClick={() => { saveBlob(preview); toast.success("Fotoboek gedownload! 📖"); }}
                className="rounded-full bg-primary hover:bg-[#1B4332] h-9">
                <Download className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Download</span>
              </Button>
              <button data-testid="photobook-preview-close" onClick={() => setPreview(null)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg transition-[background-color] duration-150">✕</button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center px-2 pb-2">
            <Document file={preview} loading={<Loader2 className="w-8 h-8 text-white animate-spin" />}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              error={<p className="text-white/80 text-sm">Voorbeeld kon niet geladen worden — probeer Download PDF</p>}>
              <Page pageNumber={pageNum} width={Math.min(980, window.innerWidth - 32)}
                renderTextLayer={false} renderAnnotationLayer={false}
                className="rounded-lg overflow-hidden shadow-2xl" />
            </Document>
          </div>
          <div className="flex items-center justify-center gap-4 pb-5 shrink-0">
            <button data-testid="photobook-preview-prev" onClick={() => setPageNum((p) => Math.max(1, p - 1))} disabled={pageNum <= 1}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center disabled:opacity-30 transition-[background-color] duration-150">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span data-testid="photobook-preview-counter" className="text-white/90 font-mono text-sm font-bold w-28 text-center">
              pagina {pageNum}{numPages ? ` / ${numPages}` : ""}
            </span>
            <button data-testid="photobook-preview-next" onClick={() => setPageNum((p) => Math.min(numPages || p + 1, p + 1))} disabled={numPages > 0 && pageNum >= numPages}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center disabled:opacity-30 transition-[background-color] duration-150">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
